import { withTheming, Inset, Spacing, Shape, getFocusStyles, Shadows } from '@doordash/design-language';
import styled, { css } from 'styled-components';
import { SizeSemantics } from './constants.js';
import { ButtonType, ButtonPadding, ButtonSize } from './types.js';
import { colorForType } from './utils.js';
import { generateShouldForwardPropsConfig } from '@doordash/utilities';

var _KIND_MAPPING;

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// decide to make this themable in the future

var RAISED_TRANSFORM = 'transform: scale(1)';
var PILL_BORDER_RADIUS = '24px';

var getPadding = function getPadding(props) {
  if (props.kind === ButtonType.Link) {
    return 0;
  }

  switch (props.padding) {
    case ButtonPadding.Wide:
      return props.size * 2;

    case ButtonPadding.None:
      return 0;

    default:
      if (props.size === ButtonSize.Small || props.size === ButtonSize.Medium) {
        return Spacing.XSmall;
      }

      return Spacing.Small;
  }
};

var getDisplay = function getDisplay(props) {
  return props.isInline ? 'inline-flex' : 'flex';
};

var getWidth = function getWidth(props) {
  var themeHeight = props.theme.Size[SizeSemantics[props.size]];

  if (props.shape === Shape.Circle) {
    return "".concat(themeHeight, "px");
  }

  return props.isInline ? 'auto' : '100%';
};

var getJustifyContent = function getJustifyContent(props) {
  return props.isInline ? 'flex-start' : 'center';
};

var getShapeBorderRadius = function getShapeBorderRadius(props) {
  // work out if this is an Icon, Toggle or regular button so we can fetch the correct
  // border radius token if the shape is rectangle
  var buttonType = props.isIconButton ? 'IconButton' : props.isToggleable ? 'ButtonToggle' : 'Button';

  switch (props.shape) {
    case Shape.Pill:
      return PILL_BORDER_RADIUS;

    case Shape.Circle:
      return '100%';

    case Shape.Rectangle:
    default:
      return "".concat(props.theme.BorderRadius["".concat(buttonType, "Default")], "px");
  }
};

var getBorderRadius = function getBorderRadius(props) {
  switch (props.kind) {
    case ButtonType.Full:
      return 0;

    case ButtonType.PrimaryPill:
    case ButtonType.SecondaryPill:
    case ButtonType.TertiaryPill:
      return PILL_BORDER_RADIUS;

    case ButtonType.Plain:
    default:
      return getShapeBorderRadius(props);
  }
};

var isDisabled = function isDisabled(props) {
  return props.disabled || props['aria-disabled'] === true;
};

var getDisabledStyle = function getDisabledStyle(props) {
  if (isDisabled(props)) {
    return css(["&:hover{cursor:not-allowed;}&:focus{box-shadow:none;}"]);
  }

  return null;
};

var KIND_MAPPING = (_KIND_MAPPING = {}, _defineProperty(_KIND_MAPPING, ButtonType.Primary, 'Primary'), _defineProperty(_KIND_MAPPING, ButtonType.PrimaryPill, 'Primary'), _defineProperty(_KIND_MAPPING, ButtonType.Full, 'Primary'), _defineProperty(_KIND_MAPPING, ButtonType.Secondary, 'Secondary'), _defineProperty(_KIND_MAPPING, ButtonType.SecondaryPill, 'Secondary'), _defineProperty(_KIND_MAPPING, ButtonType.Tertiary, 'Tertiary'), _defineProperty(_KIND_MAPPING, ButtonType.TertiaryPill, 'Tertiary'), _defineProperty(_KIND_MAPPING, ButtonType.Destructive, 'Destructive'), _KIND_MAPPING);

var getFilledColorSet = function getFilledColorSet(_ref) {
  var isToggleable = _ref.isToggleable,
      isToggled = _ref.isToggled,
      kind = _ref.kind,
      theme = _ref.theme;
  var themeColors = theme.Colors;
  var type = KIND_MAPPING[kind];
  return {
    "default": !isToggleable ? themeColors["Button".concat(type, "Background")] : !isToggled ? themeColors["Button".concat(type, "ToggleBackgroundOff")] : themeColors["Button".concat(type, "ToggleBackground")],
    hovered: !isToggleable ? themeColors["Button".concat(type, "BackgroundHovered")] : !isToggled ? themeColors["Button".concat(type, "ToggleBackgroundOffHovered")] : themeColors["Button".concat(type, "ToggleBackgroundHovered")],
    pressed: !isToggleable ? themeColors["Button".concat(type, "BackgroundPressed")] : !isToggled ? themeColors["Button".concat(type, "ToggleBackgroundOffPressed")] : themeColors["Button".concat(type, "ToggleBackgroundPressed")],
    disabled: !isToggleable ? themeColors["Button".concat(type, "BackgroundDisabled")] : !isToggled ? themeColors["Button".concat(type, "ToggleBackgroundOffDisabled")] : themeColors["Button".concat(type, "ToggleBackgroundDisabled")],
    border: !isToggleable ? type === 'Tertiary' ? themeColors["ButtonTertiaryBorder"] : 'transparent' : !isToggled ? themeColors["Button".concat(type, "ToggleBorderOff")] : themeColors["Button".concat(type, "ToggleBorderOn")],
    borderHovered: !isToggleable ? type === 'Tertiary' ? themeColors["ButtonTertiaryBorderHovered"] : 'transparent' : !isToggled ? themeColors["Button".concat(type, "ToggleBorderOffHovered")] : themeColors["Button".concat(type, "ToggleBorderOnHovered")],
    borderPressed: !isToggleable ? type === 'Tertiary' ? themeColors["ButtonTertiaryBorderPressed"] : 'transparent' : !isToggled ? themeColors["Button".concat(type, "ToggleBorderOffPressed")] : themeColors["Button".concat(type, "ToggleBorderOnPressed")],
    borderDisabled: !isToggleable ? type === 'Tertiary' ? themeColors["ButtonTertiaryBorderDisabled"] : 'transparent' : !isToggled ? themeColors["Button".concat(type, "ToggleBorderOffDisabled")] : themeColors["Button".concat(type, "ToggleBorderOnDisabled")],
    foregroundHovered: !isToggleable ? themeColors["Button".concat(type, "ForegroundHovered")] : !isToggled ? themeColors["Button".concat(type, "ToggleForegroundOffHovered")] : themeColors["Button".concat(type, "ToggleForegroundHovered")],
    foregroundPressed: !isToggleable ? themeColors["Button".concat(type, "ForegroundPressed")] : !isToggled ? themeColors["Button".concat(type, "ToggleForegroundOffPressed")] : themeColors["Button".concat(type, "ToggleForegroundPressed")],
    foregroundDisabled: !isToggleable ? themeColors["Button".concat(type, "ForegroundDisabled")] : !isToggled ? themeColors["Button".concat(type, "ToggleForegroundOffDisabled")] : themeColors["Button".concat(type, "ToggleForegroundDisabled")]
  };
};

var getColorSet = function getColorSet(props) {
  var themeColors = props.theme.Colors;

  switch (props.kind) {
    case ButtonType.FlatPrimary:
      return {
        disabled: themeColors.ButtonFlatPrimaryForegroundDisabled,
        hovered: themeColors.ButtonFlatPrimaryForegroundHovered,
        pressed: themeColors.ButtonFlatPrimaryForegroundPressed
      };

    case ButtonType.FlatSecondary:
      return {
        disabled: themeColors.ButtonFlatSecondaryForegroundDisabled,
        hovered: themeColors.ButtonFlatSecondaryForegroundHovered,
        pressed: themeColors.ButtonFlatSecondaryForegroundPressed
      };

    case ButtonType.Link:
      return {
        disabled: themeColors.ButtonLinkForegroundDisabled,
        hovered: themeColors.ButtonLinkForegroundHovered,
        pressed: themeColors.ButtonLinkForegroundPressed
      };

    case ButtonType.Primary:
    case ButtonType.PrimaryPill:
    case ButtonType.Full:
    case ButtonType.Destructive:
    case ButtonType.Secondary:
    case ButtonType.SecondaryPill:
    case ButtonType.Tertiary:
    case ButtonType.TertiaryPill:
      return getFilledColorSet(props);

    case ButtonType.Plain:
      return {
        "default": props.isRaised ? themeColors.ButtonFabBackground : props.isToggleable ? props.isToggled ? themeColors.ButtonPlainToggleBackground : themeColors.ButtonPlainToggleBackgroundOff : themeColors.ButtonPlainBackground,
        hovered: props.isRaised ? themeColors.ButtonFabBackgroundHovered : props.isToggleable ? props.isToggled ? themeColors.ButtonPlainToggleBackgroundHovered : themeColors.ButtonPlainToggleBackgroundOffHovered : themeColors.ButtonPlainBackgroundHovered,
        pressed: props.isRaised ? themeColors.ButtonFabBackgroundPressed : props.isToggleable ? props.isToggled ? themeColors.ButtonPlainToggleBackgroundPressed : themeColors.ButtonPlainToggleBackgroundOffPressed : themeColors.ButtonPlainBackgroundPressed,
        disabled: props.isRaised ? themeColors.ButtonFabBackgroundDisabled : props.isToggleable ? props.isToggled ? themeColors.ButtonPlainToggleBackgroundDisabled : themeColors.ButtonPlainToggleBackgroundOffDisabled : themeColors.ButtonPlainBackgroundDisabled,
        border: 'transparent',
        borderHovered: 'transparent',
        borderPressed: 'transparent',
        borderDisabled: 'transparent',
        foregroundHovered: !props.isToggleable ? themeColors.ButtonPlainForegroundHovered : !props.isToggled ? themeColors.ButtonPlainToggleForegroundOffHovered : themeColors.ButtonPlainToggleForegroundHovered,
        foregroundPressed: !props.isToggleable ? themeColors.ButtonPlainForegroundPressed : !props.isToggled ? themeColors.ButtonPlainToggleForegroundOffPressed : themeColors.ButtonPlainToggleForegroundPressed,
        foregroundDisabled: !props.isToggleable ? themeColors.ButtonPlainForegroundDisabled : !props.isToggled ? themeColors.ButtonPlainToggleForegroundOffDisabled : themeColors.ButtonPlainToggleForegroundDisabled
      };

    default:
      throw Error("Unknown Button type provided: ".concat(props.kind));
  }
};

var getButtonBorderWidth = function getButtonBorderWidth(props) {
  var themeBorderWidth = props.theme.BorderWidth;

  if (themeBorderWidth && props.isToggleable) {
    var kind = KIND_MAPPING[props.kind];

    if (props.isToggled) {
      return themeBorderWidth["Button".concat(kind, "Toggle")];
    }

    return themeBorderWidth["Button".concat(kind, "ToggleOff")];
  }

  return 1;
};

var getButtonStyle = function getButtonStyle(props) {
  var buttonBorder = "".concat(getButtonBorderWidth(props), "px");
  var themeColors = props.theme.Colors;

  switch (props.kind) {
    case ButtonType.FlatPrimary:
    case ButtonType.FlatSecondary:
      return css(["background-color:transparent;box-shadow:0px 0px 0px 3px rgba(165,211,242,0);color:", ";*{transition:color 0.15s ease-in-out;}&&:focus{", ";}&:hover{color:", ";}&:active{color:", ";}", ";"], themeColors[colorForType({
        isToggleable: props.isToggleable,
        isToggled: props.isToggled,
        buttonType: props.kind,
        isDisabled: isDisabled(props),
        isRaised: props.isRaised
      })], getFocusStyles({
        theme: props.theme
      }), getColorSet(props).hovered, getColorSet(props).pressed, isDisabled(props) && css(["", ";&:hover{color:", ";}&:active{color:", ";}"], getDisabledStyle, getColorSet(props).disabled, getColorSet(props).disabled));

    case ButtonType.Primary:
    case ButtonType.PrimaryPill:
    case ButtonType.Full:
    case ButtonType.Destructive:
    case ButtonType.Secondary:
    case ButtonType.SecondaryPill:
    case ButtonType.Tertiary:
    case ButtonType.TertiaryPill:
    case ButtonType.Plain:
      {
        var border = "inset 0 0 0 1px ".concat(getColorSet(props).border);
        return css(["background-color:", ";box-shadow:inset 0 0 0 ", " ", " ", ";color:", ";&&:focus{", ";}&:hover{background-color:", ";box-shadow:inset 0 0 0 ", " ", " ", ";color:", ";}&:active{background-color:", ";box-shadow:inset 0 0 0 ", " ", " ", ";", ";color:", ";}", ";"], props.isDepressed ? getColorSet(props).pressed : getColorSet(props)["default"], buttonBorder, getColorSet(props).border, props.isRaised ? ", ".concat(Shadows.Base) : '', themeColors[colorForType({
          isToggleable: props.isToggleable,
          isToggled: props.isToggled,
          buttonType: props.kind,
          isDisabled: isDisabled(props),
          isRaised: props.isRaised
        })], getFocusStyles({
          theme: props.theme,
          boxShadows: props.isRaised ? [border, Shadows.Base] : [border]
        }), getColorSet(props).hovered, buttonBorder, getColorSet(props).borderHovered, props.isRaised ? ", ".concat(Shadows.Base) : '', getColorSet(props).foregroundHovered, getColorSet(props).pressed, buttonBorder, getColorSet(props).borderPressed, props.isRaised ? ", ".concat(Shadows.Small) : '', props.isRaised && RAISED_TRANSFORM, function (props) {
          return getColorSet(props).foregroundPressed;
        }, isDisabled(props) && css(["", ";background-color:", ";box-shadow:inset 0 0 0 ", " ", " ", ";color:", ";&:hover{background-color:", ";box-shadow:inset 0 0 0 ", " ", " ", ";color:", ";}&:active{background-color:", ";box-shadow:inset 0 0 0 ", " ", " ", ";", ";color:", ";}"], getDisabledStyle, getColorSet(props).disabled, buttonBorder, getColorSet(props).borderDisabled, props.isRaised ? ", ".concat(Shadows.Base) : '', getColorSet(props).foregroundDisabled, getColorSet(props).disabled, buttonBorder, getColorSet(props).borderDisabled, props.isRaised ? ", ".concat(Shadows.Base) : '', getColorSet(props).foregroundDisabled, getColorSet(props).disabled, buttonBorder, getColorSet(props).borderDisabled, props.isRaised ? ", ".concat(Shadows.Small) : '', props.isRaised && RAISED_TRANSFORM, getColorSet(props).foregroundDisabled));
      }

    case ButtonType.Link:
      return css(["background-color:transparent;box-shadow:none;color:", ";&&:focus{", ";}&:hover{color:", ";}&:active{color:", ";}", ";"], themeColors[colorForType({
        isToggleable: props.isToggleable,
        isToggled: props.isToggled,
        buttonType: props.kind,
        isDisabled: isDisabled(props),
        isRaised: props.isRaised
      })], getFocusStyles({
        theme: props.theme
      }), getColorSet(props).hovered, getColorSet(props).pressed, isDisabled(props) && css(["", ";&:hover{color:", ";}&:active{color:", ";}"], getDisabledStyle, getColorSet(props).disabled, getColorSet(props).disabled));

    default:
      return null;
  }
};

var StyledButtonRoot = styled.button.withConfig(generateShouldForwardPropsConfig(['isToggled', 'isToggleable', 'isDepressed', 'isInline', 'isRaised'])).withConfig({
  displayName: "styles__StyledButtonRoot",
  componentId: "sc-1ldytso-0"
})(["position:relative;max-width:100%;margin:0;padding:0;display:", ";", " width:", ";align-items:center;justify-content:", ";border-radius:", ";border:none;cursor:pointer;transition:background-color 0.15s ease-in-out,box-shadow 0.15s ease-in-out;user-select:none;text-decoration:none;text-align:center;&::-moz-focus-inner{outline:none;border-style:none;padding:0;}", ";", ";", ";"], getDisplay, function (props) {
  return props.padding !== ButtonPadding.None && css(["min-height:", "px;"], props.theme.Size[SizeSemantics[props.size]]);
}, getWidth, getJustifyContent, getBorderRadius, getDisabledStyle, getButtonStyle, function (props) {
  return props.isIconButton && css(["height:", "px;width:", "px;padding:0;align-items:center;justify-content:center;"], props.theme.Size[SizeSemantics[props.size]], props.theme.Size[SizeSemantics[props.size]]);
});
var ButtonRoot = withTheming(StyledButtonRoot);
var SpinnerContainer = styled.span.withConfig({
  displayName: "styles__SpinnerContainer",
  componentId: "sc-1ldytso-1"
})(["position:absolute;top:0;left:0;height:100%;width:100%;display:flex;align-items:center;justify-content:center;"]);

var getOpacityForVisibility = function getOpacityForVisibility(props) {
  return props.isHidden ? 0 : 1;
};

var ContentWrapper = styled(Inset).attrs(function (props) {
  return {
    verticalSize: Inset.Sizes.None,
    horizontalSize: getPadding(props)
  };
}).withConfig({
  displayName: "styles__ContentWrapper",
  componentId: "sc-1ldytso-2"
})(["display:block;flex-grow:1;max-width:100%;transition:opacity 0.15s ease-in-out;opacity:", ";"], getOpacityForVisibility);
var MainContentContainer = styled.span.withConfig({
  displayName: "styles__MainContentContainer",
  componentId: "sc-1ldytso-3"
})(["flex-grow:2;min-width:0;display:flex;justify-content:center;& > *{width:100%;}"]);
var SubContentContainer = styled.span.withConfig({
  displayName: "styles__SubContentContainer",
  componentId: "sc-1ldytso-4"
})(["flex-grow:1;flex-shrink:2;", " min-width:0;display:flex;justify-content:", ";"], function (props) {
  return !props.isInline && css(["flex-basis:", ";"], function (props) {
    return props.isOnlyIcons ? props.iconSize + 'px' : '25%';
  });
}, function (props) {
  return props.side === 'leading' ? 'flex-start' : 'flex-end';
});
var SubContentIconContainer = styled.span.withConfig({
  displayName: "styles__SubContentIconContainer",
  componentId: "sc-1ldytso-5"
})(["width:", "px;display:flex;align-items:center;justify-content:center;"], function (_ref2) {
  var iconSize = _ref2.iconSize;
  return iconSize || 24;
});

export { ButtonRoot, ContentWrapper, MainContentContainer, SpinnerContainer, SubContentContainer, SubContentIconContainer };
